<script setup>
import EnDialog from 'Components/base/EnDialog/EnDialog';
import EnButton from 'Components/base/EnButton/EnButton.vue';
import EnSnackbar from 'Components/feedback/EnSnackbar/EnSnackbar.vue';
import EnDataGrid from 'Components/datagrid/EnDataGrid';
import EnPageTitle from 'Components/layout/EnPageTitle/EnPageTitle.vue';
import EnSmallLabeledContent from 'Components/text/EnSmallLabeledContent/EnSmallLabeledContent';
import Breadcrumbs from 'Components/navigation/Breadcrumbs/Breadcrumbs.vue';
import Header from 'Components/datagrid/types/header.ts';
import { useRoute } from 'vue-router';

import { SnackbarType } from 'Components/types/SnackbarType';

const route = useRoute();
const successMessage = ref();
const asyncDataGrid = ref();

const credentialsGenerated = ref(false);
const accountName = ref(null);
const accountId = ref(route.query.id);
const enPowerAccountId = ref(route.query.enPowerId);
const items = ref([]);
const generatedModel = ref(null);
const urls = ref(null);
const getUrl = computed(() => {
  const parameterObject = {};
  if (accountId.value) {
    parameterObject.id = accountId.value;
  }
  if (enPowerAccountId.value) {
    parameterObject.enPowerId = enPowerAccountId.value;
  }
  const params = new URLSearchParams(parameterObject);
  return `/api/ApiClients/ApiClients?${params.toString()}`;
});

const breadcrumbInfo = computed(() => {
  const route = accountId.value ? {
    href: `${urls.value?.engineUrl}/EnFactsAccounts?Name=${accountName.value}`,
    value: t('ApiClients.url.enFactsAccounts')
  } : {
    href: `${urls.value?.engineUrl}/EnPowerAccounts`,
    value: t('ApiClients.url.enPowerAccounts')
  };
  return [route, t('ApiClients.title')];
});

const headers = computed(() => {
  return [
    new Header({
      title: t('ApiClients.clientId'),
      value: 'clientId',
      sortable: true,
      minWidth: '200px'
    }),
    new Header({
      title: t('ApiClients.clientName'),
      value: 'clientName',
      sortable: true,
      minWidth: '200px'
    }),
  ];
});

async function loadItems() {
  const itemsResponse = await fetch(getUrl.value, {
    method: 'GET',
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json'
    },
  });
  if (itemsResponse.ok) {
    items.value = await itemsResponse.json();
  }
}

function exportItems() {
  asyncDataGrid.value.exportData(items.value);
}

async function generateCredentials() {
  credentialsGenerated.value = true;
  const parameterObject = {};
  if (accountId.value) {
    parameterObject.id = accountId.value;
  }
  if (enPowerAccountId.value) {
    parameterObject.enPowerId = enPowerAccountId.value;
  }
  const params = new URLSearchParams(parameterObject);
  const response = await fetch(`/api/ApiClients/GenerateClient?${params}`, {
    method: 'POST',
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json'
    },
  });
  if (response.ok) {
    successMessage.value.pushMessage(t('common.snackbar.saved'));
    generatedModel.value = await response.json();
    await loadItems();
  }
}
provide('reload', {loadItems});

onBeforeMount(async () => {
  const requestBody = {accountId: accountId.value};
  const response = await fetch(`/api/Configuration/GetConfigurationForUser`, {
    method: 'POST',
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(requestBody),
  });
  if (response.ok) {
    const config = await response.json();
    accountName.value = config.EnFactsAccountName;
    urls.value = {engineUrl: config.EngineUrl, authUrl: config.AuthUrl};
  }
  await loadItems();
});
</script>

<template>
  <div>
    <breadcrumbs v-if="urls" :items="breadcrumbInfo"></breadcrumbs>
    <en-snackbar ref="successMessage" :type="SnackbarType.SUCCESS"></en-snackbar>
    <en-page-title>
      {{ t('ApiClients.title') }}
      <template v-slot:actions>
        <en-button
            class="general-action-button"
            static-id="button-generate-credentials"
            prepend-icon="mdi mdi-send"
            @click="generateCredentials"
        >
          {{ t('ApiClients.generateText') }}
        </en-button>
      </template>
    </en-page-title>
    
    <div class="flex flex-wrap flex-auto">

      <div class="w-full p-3 grow-0 shrink-0 basis-full max-w-full" v-if="accountId || enPowerAccountId">
        <en-data-grid
            ref="asyncDataGrid"
            :export-file-name="t('ApiClients.exportFileName', {account: accountName})"
            :headers="headers"
            :items="items"
            preserve-column-width
            show-export-button
            @export-all-data="exportItems"
        >

        </en-data-grid>
      </div>

      <en-dialog v-model="credentialsGenerated" :title="t('ApiClients.generateText')">
        <template #content>
          <en-small-labeled-content :title="t('ApiClients.generateTitle')" v-if="generatedModel">
            <div
                v-html="t('ApiClients.generatedText', {clientId: generatedModel.clientId, clientSecret: generatedModel.secret})"></div>
          </en-small-labeled-content>
        </template>
      </en-dialog>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use "Styles/helper/variables.scss";

.status-bar {
  width: 100%;
  height: 100%;
}

.form-group-bordered h5 {
  margin-bottom: 50px;
}

.btn.btn-filled {
  display: inline-block;
  text-align: center;
}

.validation-error {
  background: variables.$en-red;
  color: variables.$en-0;
  padding: 3px 5px;
  margin-bottom: 20px;
}

.form-group {
  margin-top: 0;
}
</style>